"use client";

import type { CSSProperties } from "react";
import { useShallow } from "zustand/react/shallow";
import { Info } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import {
    ButtonAppearance,
    ButtonBlock as ButtonBlockType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import {
    useBlockEditable,
    useBlockThumbnail,
} from "../../../hooks/useBlockEditable";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import { usePlayerGoToPage } from "../../../hooks/usePlayerGoToPage";
import {
    BUTTON_CONFIG,
    ButtonTarget,
    buttonColors,
    resolveButtonAppearance,
    resolveButtonTarget,
} from "../../../lib/blockRegistry";
import { PreviewButton } from "../shared/PreviewButton";
import { DraftTextarea } from "../shared/draftTextarea";

type Look = { className: string; style: CSSProperties };

function buttonLook(a: ButtonAppearance | undefined): Look {
    const { style, size } = resolveButtonAppearance(a);
    const filled = style === "filled";
    const { color, textColor } = buttonColors(a, BUTTON_CONFIG.theme);
    return {
        className: cn(
            "max-w-full whitespace-normal break-words border-2 border-[color:var(--button-color)] text-center text-[color:var(--button-text)] hover:opacity-90",
            filled
                ? "bg-[color:var(--button-color)] hover:bg-[color:var(--button-color)]"
                : "bg-transparent hover:bg-transparent",
            BUTTON_CONFIG.sizeClass[size]
        ),
        style: {
            "--button-color": color,
            "--button-text": textColor,
        } as CSSProperties,
    };
}

export default function ButtonBlock({ block }: { block: ButtonBlockType }) {
    const editable = useBlockEditable();
    const locale = useLomdaLocale();
    const { dir: uiDir } = useLocale();
    const pageIds = useLomdaV3Editor(
        useShallow((s) => s.content.pages.map((p) => p.id))
    );
    const appearance = resolveButtonAppearance(block.data?.appearance);
    const target = resolveButtonTarget(block.data, pageIds);
    const look = buttonLook(block.data?.appearance);
    const defaultLabel = t("lomda-v3-editor.button.default-label", locale);
    const hasTarget = target.kind === "url" || target.kind === "page";

    if (!editable && !hasTarget) return null;

    return (
        <div className="flex flex-col gap-2">
            <div
                className={cn(
                    "flex",
                    BUTTON_CONFIG.alignClass[appearance.align]
                )}
            >
                {editable ? (
                    <EditorButton
                        block={block}
                        look={look}
                        placeholder={defaultLabel}
                    />
                ) : (
                    <LearnerButton
                        target={target}
                        look={look}
                        label={block.data.label ?? ""}
                    />
                )}
            </div>
            {editable && !hasTarget && (
                <p
                    dir={uiDir}
                    className="lomda-v3-chrome flex items-center justify-center gap-1.5 text-center text-xs text-[hsl(var(--lomda-muted-text))]"
                >
                    <Info className="size-3.5 shrink-0" />
                    {t(
                        target.kind === "missing-page"
                            ? "lomda-v3-editor.button.hint.page-missing"
                            : "lomda-v3-editor.button.hint.no-action"
                    )}
                </p>
            )}
        </div>
    );
}

function EditorButton({
    block,
    look,
    placeholder,
}: {
    block: ButtonBlockType;
    look: Look;
    placeholder: string;
}) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);

    return (
        <PreviewButton
            asChild
            className={cn(look.className, "cursor-text")}
            style={look.style}
        >
            <div
                onClick={(e) =>
                    e.currentTarget.querySelector("textarea")?.focus()
                }
            >
                <DraftTextarea
                    value={block.data.label}
                    placeholder={placeholder}
                    fillWhenLeftBlank={placeholder}
                    aria-label={t("lomda-v3-editor.button.label")}
                    cols={1}
                    className="text-center"
                    onCommit={(label) =>
                        patchBlockData(block.id, "button", () => ({ label }))
                    }
                />
            </div>
        </PreviewButton>
    );
}

function LearnerButton({
    target,
    look,
    label,
}: {
    target: ButtonTarget;
    look: Look;
    label: string;
}) {
    const locale = useLomdaLocale();
    const goToPage = usePlayerGoToPage();
    const thumbnail = useBlockThumbnail();

    // A page thumbnail is itself a button: nothing inside may take focus.
    if (thumbnail) {
        return (
            <PreviewButton
                asChild
                className={look.className}
                style={look.style}
            >
                <span>{label}</span>
            </PreviewButton>
        );
    }

    if (target.kind === "url") {
        return (
            <PreviewButton
                asChild
                className={look.className}
                style={look.style}
            >
                <a
                    href={target.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onKeyDown={(e) => {
                        if (e.key !== " ") return;
                        e.preventDefault();
                        e.currentTarget.click();
                    }}
                >
                    {label}{" "}
                    <span className="sr-only">
                        {t("lomda-v3-editor.button.opens-new-tab", locale)}
                    </span>
                </a>
            </PreviewButton>
        );
    }

    if (target.kind !== "page") return null;
    return (
        <PreviewButton
            type="button"
            className={look.className}
            style={look.style}
            onClick={() => goToPage?.(target.pageId)}
        >
            {label}
        </PreviewButton>
    );
}
